<script setup lang="ts">
import VimeoPlayer from '@vimeo/player';
import type {
  Options as VimeoPlayerOptions,
  PlaybackRateEvent,
  TextTrackChangeEvent,
  TimeEvent,
  VolumeChangeEvent,
} from 'vimeo__player';
import { throttle } from 'lodash-es';

const COMPLETION_THRESHOLD = 85; // Percentage of video watched to be considered completed.

const {
  autoplay,
  volume,
  playbackRate,
  captions,
  language,
  setVolume,
  setPlaybackRate,
  setCaptions,
  setLanguage,
} = useUserPreferences();
const route = useRoute();
const { locale } = useI18n();

const emittedCompleted = ref(false);
const playerEl = ref<HTMLElement | null>(null);
const playerElementWait = ref<NodeJS.Timeout | null>(null);
const initializing = ref(true);
let playerInstance: VimeoPlayer | null = null;
let progress = 0;

const emit = defineEmits<{
  (e: 'ready'): void;
  (e: 'play'): void;
  (e: 'pause'): void;
  (e: 'ended'): void;
  (e: 'completed'): void;
  (e: 'progress', percent: number): void;
}>();

const props = withDefaults(
  defineProps<{
    vimeoId: string;
    completionPercentageThreshold?: number;
    forceDisableAutoplay?: boolean;
    thumbnail?: string;
    loop?: boolean;
  }>(),
  {
    completionPercentageThreshold: COMPLETION_THRESHOLD,
    forceDisableAutoplay: false,
    thumbnail: undefined,
    loop: false,
  },
);

const isDisplayingActivityInformation = route.hash === '#activity-information';

const shouldAutoplay = computed(() => {
  if (props.forceDisableAutoplay || isDisplayingActivityInformation) {
    return false;
  }

  return autoplay.value;
});

const mountPlayer = () => {
  if (playerEl.value === null) {
    // Wait for player element to be available.
    playerElementWait.value = setTimeout(mountPlayer, 100);
  }

  const embedOptions = {
    pip: false,
    controls: true,
    autoplay: shouldAutoplay.value,
    color: '#58bef3',
    loop: props.loop,
    audiotrack: locale.value,
    texttrack: locale.value,
  } as VimeoPlayerOptions;

  if (props.vimeoId.startsWith('http')) {
    embedOptions.url = props.vimeoId + `?lang=${locale.value}`;
  } else {
    embedOptions.id = parseInt(props.vimeoId, 10);
  }

  playerInstance = new VimeoPlayer(playerEl.value!, embedOptions);
  playerInstance.ready().finally(() => {
    initializing.value = false;
  });

  applyStoredPreferences();
  attachPlayerEvents();
};

const restart = () => {
  if (playerInstance === null) {
    return;
  }

  playerInstance.setCurrentTime(0);
  playerInstance.play();
};

const applyStoredPreferences = () => {
  if (playerInstance === null) {
    return;
  }

  playerInstance.setPlaybackRate(playbackRate.value);
  playerInstance.setVolume(volume.value);
  if (captions.value) {
    playerInstance.enableTextTrack(language.value, 'captions');
  } else {
    playerInstance.disableTextTrack();
  }
};

const progressThrottle = throttle(percent => {
  emit('progress', percent);
}, 2000);

const attachPlayerEvents = () => {
  if (playerInstance === null) {return;}

  playerInstance.on('ready', () => {
    emit('ready');
  });

  playerInstance.on('play', () => {
    emit('play');
    emittedCompleted.value = false;
  });

  playerInstance.on('pause', () => {
    emit('pause');
  });

  playerInstance.on('ended', () => {
    emit('ended');
  });

  playerInstance.on('playbackratechange', (event: PlaybackRateEvent) => {
    setPlaybackRate(event.playbackRate);
  });

  playerInstance.on('volumechange', (event: VolumeChangeEvent) => {
    setVolume(event.volume);
  });

  playerInstance.on('texttrackchange', (event: TextTrackChangeEvent) => {
    if (event.language === null) {
      setCaptions(false);
    } else {
      setCaptions(true);
      setLanguage(event.language);
    }
  });

  playerInstance.on('timeupdate', (event: TimeEvent) => {
    const { percent } = event; // 0-1 float percentage of video watched.

    progress = percent;
    progressThrottle(percent);

    if (percent * 100 >= props.completionPercentageThreshold && !emittedCompleted.value) {
      emit('completed');
      emittedCompleted.value = true;
    }
  });
};

onMounted(() => {
  mountPlayer();
});

onBeforeUnmount(() => {
  if (playerElementWait.value) {
    clearTimeout(playerElementWait.value);
  }

  progressThrottle(progress);
  progressThrottle.cancel();
});

defineExpose({
  restart,
});
</script>

<template>
  <div class="relative flex items-center justify-center aspect-video w-full h-full overflow-hidden lg:rounded-lg">
    <div
      class="relative w-full h-full flex items-center justify-center"
      :class="{'hidden': !initializing}"
    >
      <div
        v-if="thumbnail"
        :style="{backgroundImage: `url(${thumbnail})`}"
        class="w-full h-full bg-center bg-cover placeholder absolute opacity-50"
      />
      <div class="z-1">
        <Loader />
      </div>
    </div>

    <div
      ref="playerEl"
      :class="{'hidden': initializing}"
      class="vimeo"
    />
  </div>
</template>

<style lang="scss">
.vimeo,
iframe {
  width: 100%;
  height: 100%;
  border: none;
  aspect-ratio: 16/9;
}

.placeholder {
  filter: blur(10px);
}
</style>